import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { onIdTokenChanged, signOut as fbSignOut, type User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { paths, type Role } from "@clevis/shared-types";
import { auth, db } from "../lib/firebase";

export type AuthStatus = "loading" | "anonymous" | "unprovisioned" | "customer" | "super_admin";

export interface Impersonation {
  tenantId: string;
  reason: string;
  startedAt: number;
  expiresAt: number;
  extensionCount: number;
}

export interface AuthState {
  status: AuthStatus;
  user: User | null;
  tenantId: string | null;
  role: Role | null;
  superAdmin: boolean;
  /** Owner-set access: sections taken away from / added to the role's defaults. */
  access: { deny: string[]; grant: string[] };
  /** Set while a super admin is "logged in as" a tenant. */
  impersonation: Impersonation | null;
  refreshClaims: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

interface ClaimState {
  status: AuthStatus;
  user: User | null;
  tenantId: string | null;
  role: Role | null;
  superAdmin: boolean;
  access: { deny: string[]; grant: string[] };
}
const NO_ACCESS_CHANGES = { deny: [] as string[], grant: [] as string[] };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState<ClaimState>({
    status: "loading",
    user: null,
    tenantId: null,
    role: null,
    superAdmin: false,
    access: NO_ACCESS_CHANGES,
  });
  const [impersonation, setImpersonation] = useState<Impersonation | null>(null);

  const applyUser = useCallback(async (user: User | null, forceRefresh = false) => {
    if (!user) {
      setClaims({ status: "anonymous", user: null, tenantId: null, role: null, superAdmin: false, access: NO_ACCESS_CHANGES });
      return;
    }
    const token = await user.getIdTokenResult(forceRefresh);
    const superAdmin = token.claims.superAdmin === true;
    const tenantId = typeof token.claims.tenantId === "string" ? token.claims.tenantId : null;
    const role = typeof token.claims.role === "string" ? (token.claims.role as Role) : null;
    const list = (v: unknown) => (Array.isArray(v) ? (v as string[]) : []);
    setClaims({
      status: superAdmin ? "super_admin" : tenantId ? "customer" : "unprovisioned",
      user,
      tenantId,
      role,
      superAdmin,
      access: { deny: list(token.claims.deny), grant: list(token.claims.grant) },
    });
  }, []);

  useEffect(() => {
    return onIdTokenChanged(auth, (user) => void applyUser(user));
  }, [applyUser]);

  // When the owner changes someone's role or access, the server bumps
  // claimsVersion on their /users doc; pick up the new token right away.
  useEffect(() => {
    if (!claims.user) return;
    let seen: number | null = null;
    return onSnapshot(doc(db, paths.user(claims.user.uid)), (snap) => {
      const v = (snap.get("claimsVersion") as number | undefined) ?? 0;
      if (seen !== null && v !== seen && auth.currentUser) void applyUser(auth.currentUser, true);
      seen = v;
    }, () => undefined);
  }, [claims.user, applyUser]);

  // Super admins watch their own doc for an active impersonation window.
  useEffect(() => {
    if (!claims.superAdmin || !claims.user) {
      setImpersonation(null);
      return;
    }
    return onSnapshot(doc(db, paths.superAdmin(claims.user.uid)), (snap) => {
      const imp = snap.get("impersonation") as Impersonation | null | undefined;
      setImpersonation(imp && imp.expiresAt > Date.now() ? imp : null);
    });
  }, [claims.superAdmin, claims.user]);

  // Expire the session client-side when the countdown runs out.
  useEffect(() => {
    if (!impersonation) return;
    const ms = impersonation.expiresAt - Date.now();
    if (ms <= 0) {
      setImpersonation(null);
      return;
    }
    const t = setTimeout(() => setImpersonation(null), ms);
    return () => clearTimeout(t);
  }, [impersonation]);

  const refreshClaims = useCallback(async () => {
    if (auth.currentUser) await applyUser(auth.currentUser, true);
  }, [applyUser]);

  const signOut = useCallback(async () => {
    await fbSignOut(auth);
  }, []);

  // While impersonating, the whole app sees a synthetic owner of that tenant.
  const effective: Omit<AuthState, "refreshClaims" | "signOut"> = impersonation
    ? {
        status: "customer",
        user: claims.user,
        tenantId: impersonation.tenantId,
        role: "owner",
        superAdmin: true,
        access: NO_ACCESS_CHANGES,
        impersonation,
      }
    : { ...claims, impersonation: null };

  return (
    <AuthContext.Provider value={{ ...effective, refreshClaims, signOut }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
